import classNames from "classnames";
import React, { ReactNode } from "react";
import { toast as reactToast, ToastOptions as ReactToastOptions, Zoom } from "react-toastify";
import "./_toastify-overrides.scss";
import BidSuccessfulIcon from "../../icons/BidSuccessful.svg";
import ErrorIcon from "../../icons/Error.svg";
import InfoIcon from "../../icons/Info.svg";
import WarningIcon from "../../icons/Warning.svg";

import Styles from "./Toast.module.scss";

const DefaultToastOptions: ReactToastOptions = {
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    pauseOnFocusLoss: true,
    draggable: false,
    closeButton: false,
    transition: Zoom,
    position: "bottom-center"
};

type MsgType = "info" | "success" | "warning" | "error";

const IconMap = {
    success: BidSuccessfulIcon,
    warning: WarningIcon,
    error: ErrorIcon,
    info: InfoIcon
};

interface MsgCompProps {
    type: MsgType;
    msg: ReactNode;
    className?: string;
    contentClassName?: string;
}

const MsgComp = ({ msg, className, contentClassName, type }: MsgCompProps) => {
    const Icon = IconMap[type];
    return (
        <div className={classNames(Styles.main, className)}>
            <div className={Styles.icon}>
                <Icon />
            </div>
            <div className={classNames(Styles.content, contentClassName)}>{msg}</div>
        </div>
    );
};

export interface ToastOptions {
    shouldOverrideDefaultComp?: boolean;
    className?: string;
    contentClassName?: string;
    autoClose?: ReactToastOptions["autoClose"];
    closeButton?: ReactToastOptions["closeButton"];
    hideProgressBar?: ReactToastOptions["hideProgressBar"];
    closeOnClick?: ReactToastOptions["closeOnClick"];
    pauseOnHover?: ReactToastOptions["pauseOnHover"];
    draggable?: ReactToastOptions["draggable"];
    position?: ReactToastOptions["position"];
    toastId?: ReactToastOptions["toastId"];
}

export const toast = (function () {
    function getComp(type: "info" | "success" | "warning" | "error", msg: ReactNode, options?: ToastOptions) {
        return options?.shouldOverrideDefaultComp ? (
            <>{msg}</>
        ) : (
            <MsgComp
                type={type}
                msg={msg}
                className={options?.className}
                contentClassName={options?.contentClassName}
            />
        );
    }

    function toast(msg: ReactNode, options?: ToastOptions) {
        return reactToast.info(getComp("info", msg), { ...DefaultToastOptions, ...options });
    }

    toast.info = function (msg: ReactNode, options?: ToastOptions) {
        return toast(msg, options);
    };
    toast.success = function (msg: ReactNode, options?: ToastOptions) {
        return reactToast.success(getComp("success", msg), { ...DefaultToastOptions, ...options });
    };
    toast.error = function (msg: ReactNode, options?: ToastOptions) {
        return reactToast.error(getComp("error", msg), { ...DefaultToastOptions, ...options });
    };
    toast.warning = function (msg: ReactNode, options?: ToastOptions) {
        return reactToast.warning(getComp("warning", msg), { ...DefaultToastOptions, ...options });
    };

    toast.dismiss = function (id?: string | number) {
        return reactToast.dismiss(id);
    };

    return toast;
})();
